import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { allArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-400 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-900 mb-2 font-heading">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors no-underline"
            >
              <i className="ri-home-line mr-2"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.image,
    url: `https://beiziganxi.com/article/${article.slug}`,
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`${article.categoryName},被子干洗,被芯护理,干洗知识`}
        canonicalPath={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="bg-background-100 py-10">
          <div className="container-main">
            <div className="max-w-3xl mx-auto">
              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-flex items-center px-3 py-1 bg-accent-100 text-accent-900 rounded-full text-xs font-medium no-underline whitespace-nowrap"
                  >
                    {article.categoryName}
                  </Link>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-calendar-line"></i>{article.date}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-time-line"></i>{article.readTime}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-user-line"></i>{article.author}
                  </span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4 font-heading">
                  {article.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {article.excerpt}
                </p>
              </div>

              {/* Featured Image */}
              <div className="relative rounded-2xl overflow-hidden h-64 md:h-96 mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Content */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-10 mb-10">
                <p className="text-foreground-700 leading-relaxed mb-8">
                  {article.content.intro}
                </p>

                {article.content.sections.map((section, idx) => (
                  <section key={idx} className="mb-8">
                    <h2 className="text-lg md:text-xl font-bold text-foreground-900 mb-3 font-heading">
                      {section.heading}
                    </h2>
                    <div className="text-sm text-foreground-700 leading-relaxed space-y-3">
                      {section.content.split('\n\n').map((para, pIdx) => (
                        <p key={pIdx}>{para}</p>
                      ))}
                    </div>
                  </section>
                ))}

                <div className="mt-8 p-5 bg-primary-50 border border-primary-100 rounded-xl">
                  <p className="text-sm text-foreground-700 leading-relaxed font-medium">
                    <i className="ri-lightbulb-flash-line text-primary-500 mr-2"></i>
                    <strong className="font-semibold text-foreground-900">结论：</strong>
                    {article.content.conclusion}
                  </p>
                </div>
              </div>

              {/* FAQ within Article */}
              {article.faq && article.faq.length > 0 && (
                <div className="mb-10">
                  <h3 className="text-lg font-bold text-foreground-900 mb-4 font-heading">
                    关于{article.categoryName}的常见疑问
                  </h3>
                  <div className="space-y-3">
                    {article.faq.map((item, idx) => (
                      <details
                        key={idx}
                        className="bg-background-50 border border-background-200/70 rounded-xl group cursor-pointer"
                      >
                        <summary className="px-5 py-4 text-sm font-semibold text-foreground-900 flex items-center justify-between font-heading">
                          <span className="flex items-center gap-2">
                            <i className="ri-question-line text-accent-500"></i>
                            {item.question}
                          </span>
                          <i className="ri-arrow-down-s-line transition-transform group-open:rotate-180 text-foreground-400"></i>
                        </summary>
                        <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                          {item.answer}
                        </div>
                      </details>
                    ))}
                  </div>

                  <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                      __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: article.faq.map((f) => ({
                          '@type': 'Question',
                          name: f.question,
                          acceptedAnswer: { '@type': 'Answer', text: f.answer },
                        })),
                      }),
                    }}
                  />
                </div>
              )}

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-foreground-900 mb-4 font-heading">相关文章推荐</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {relatedArticles.map((rel) => (
                      <Link
                        key={rel.slug}
                        to={`/article/${rel.slug}`}
                        className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-200 no-underline cursor-pointer"
                      >
                        <div className="h-32 overflow-hidden">
                          <img
                            src={rel.image}
                            alt={rel.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors font-heading">
                            {rel.title}
                          </h4>
                          <p className="text-xs text-foreground-400 mt-2">{rel.date}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}